// src/lib/pharmacyFirst/pathways/aht/aht-wt-foundayo-initial.pathway.ts
//
// AHT Weight Management — Orforglipron (Foundayo®) film-coated tablets, Initial Supply Pathway
// Source: AHT Foundayo PGD v2.1 (public/pgd-docs/aht/weight/). No separate Foundayo supply pathway document exists.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// This pathway covers initial supply only: 1 × Foundayo 0.8 mg tablets, 30-tablet pack (PGD Month 1).
// A patient switching from another incretin also starts here, at 0.8 mg, after the washout check below.
// Capsule transfer and re-initiation after a gap have their own pathways.

import type { Pathway } from "../../types/pathway.types";
import { buildFoundayoSupplyDownstream, foundayoRedirectOutcome, FDY_OUT_CANNOT_SUPPLY } from "./_shared/foundayo-supply-downstream";
import { FDY_SWITCH_MIN_DAYS_SINCE_LAST_DOSE, daysSinceIsoDate } from "./_shared/foundayo-route-helpers";

const CONFIRM_CARD = "fdy-init-confirmation-card";
const Q_SWITCHING = "fdy-init-q-switching";
const Q_PREVIOUS_MEDICINE = "fdy-init-q-previous-medicine";
const Q_LAST_DOSE_DATE = "fdy-init-q-last-dose-date";
const R_WASHOUT = "fdy-init-washout-route";
const Q_SWITCH_COUNSELLED = "fdy-init-q-switch-counselled";

const OUT_USE_TRANSFER = "FDY_OUT_USE_TRANSFER_ROUTE";
const OUT_SWITCH_TOO_SOON = "FDY_OUT_SWITCH_TOO_SOON";

const downstream = buildFoundayoSupplyDownstream({
  idPrefix: "fdy-init",
  attestHeading: "Confirm supply of Foundayo® 0.8 mg tablets (initiation dose)",
  attestLead: [
    "Checked the pack integrity and expiry date, confirmed the tablet imprint is G1 (0.8 mg), and that the stock came from an MHRA registered wholesaler with the supplier recorded",
  ],
  supplyTitle: "Supply complete — Foundayo® 0.8 mg tablets (initial supply)",
  supplyReason: "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
});

export const ahtWtFoundayoInitialPathway: Pathway = {
  id: "aht-wt-foundayo-initial",
  name: "Orforglipron (Foundayo®) — Initial Supply",
  population: "Adults aged 18–84 attending for initial supply of orforglipron tablets",
  pgdRefs: ["pgd-aht-wt-foundayo"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: CONFIRM_CARD,
  // Initial supply is always Month 1, and this pathway has no dose-selection node.
  fixedDoseStepId: "fdy-0_8mg",

  nodes: [
    {
      id: CONFIRM_CARD,
      type: "confirmation_card",
      heading: "Confirm answers from drug selection",
      items: [
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q4_pancreatitis",
          questionText: "History of pancreatitis (acute or chronic)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q5_t1dm_t3c",
          questionText: "Type 1 diabetes (T1DM) or Type 3c (pancreatogenic) diabetes?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q6_sulphonylureas_or_insulin",
          questionText: "Currently using insulin or a sulfonylurea (for example gliclazide, glibenclamide)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q7_severe_renal_or_hepatic_impairment",
          sourceItemId: "severe_hepatic",
          questionText: "Severe hepatic impairment (Child-Pugh C)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q13_active_suicidal_ideation",
          questionText: "Active suicidal ideation, recent self-harm, or a recent suicide attempt?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q14_severe_gi_or_gastroparesis",
          sourceItemId: "q14_severe_gi",
          questionText: "Severe or unstable gastrointestinal disease, including severe gastroparesis?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q15_pregnancy_breastfeeding_planning",
          questionText:
            "Pregnant, breastfeeding, or planning pregnancy? (Orforglipron must be stopped at least 3 weeks before a planned pregnancy.)",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q17_ciclosporin",
          sourceItemId: "q18_retinopathy_any",
          questionText: "Diabetic retinopathy (any stage) or macular oedema?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q17_ciclosporin",
          sourceItemId: "q19_nyha_iv",
          questionText: "NYHA Class IV (severe) heart failure?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_binge_eating",
          questionText: "Binge eating?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_self_induced_vomiting",
          questionText: "Self-induced vomiting?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_laxative_diuretic_misuse",
          questionText: "Laxative or diuretic use for weight control?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_compulsive_exercise",
          questionText: "Compulsive exercise?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_weight_shape_preoccupation",
          questionText: "Disproportionate preoccupation with weight or shape?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q9c_foundayo_disordered_eating_screen",
          sourceItemId: "fdy_ed_unsure",
          questionText: "Patient unsure, or an answer could not be obtained about disordered eating?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q20F_foundayo_medicines_checklist",
          sourceItemId: "fdy_excluded_medicines",
          questionText: "Taking ritonavir, telaprevir, or a strong CYP3A4 inducer (rifampicin, phenytoin, carbamazepine, St John's wort)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
        },
      ],
      next: Q_SWITCHING,
    },

    {
      id: Q_SWITCHING,
      type: "yes_no",
      text: "Is the patient switching to Foundayo from another incretin (injectable semaglutide, oral semaglutide, tirzepatide, liraglutide or similar)?",
      hint: "A switcher must have stopped the previous medicine. Orforglipron and any other incretin must never be taken together.",
      answerPolarity: "neutral",
      yes: Q_PREVIOUS_MEDICINE,
      no: downstream.entryNodeId,
    },

    {
      id: Q_PREVIOUS_MEDICINE,
      type: "single_select",
      text: "Which medicine is the patient switching from?",
      options: [
        { id: "prev_injectable_semaglutide", label: "Semaglutide injection (Wegovy®, Ozempic®)" },
        { id: "prev_oral_semaglutide", label: "Oral semaglutide (Wegovy® tablets, Rybelsus®)" },
        { id: "prev_tirzepatide", label: "Tirzepatide (Mounjaro®)" },
        { id: "prev_liraglutide", label: "Liraglutide (Saxenda®, Victoza®, Biolide®)" },
        { id: "prev_other_incretin", label: "Another incretin mimetic" },
        { id: "prev_orforglipron_capsules", label: "Orforglipron hard capsules", hint: "This is a change of formulation, not a switch. Use the capsule transfer route." },
      ],
      routing: {
        prev_injectable_semaglutide: Q_LAST_DOSE_DATE,
        prev_oral_semaglutide: Q_LAST_DOSE_DATE,
        prev_tirzepatide: Q_LAST_DOSE_DATE,
        prev_liraglutide: Q_LAST_DOSE_DATE,
        prev_other_incretin: Q_LAST_DOSE_DATE,
        prev_orforglipron_capsules: { outcomeId: OUT_USE_TRANSFER },
      },
      storesTo: "fdySwitchPreviousMedicine",
      required: true,
    },

    {
      id: Q_LAST_DOSE_DATE,
      type: "date_input",
      text: "Date of the last dose of the previous medicine",
      hint: "The first 0.8 mg tablet must be taken no sooner than 7 days after this date.",
      storesTo: "fdySwitchLastDoseDate",
      max: 0,
      next: R_WASHOUT,
    },

    {
      id: R_WASHOUT,
      type: "condition_route",
      conditions: [
        {
          // Fail safe: a missing or invalid date gives NaN, which is not at or above the minimum, so supply is blocked.
          evaluate: (ctx) => !(daysSinceIsoDate(ctx.context["fdySwitchLastDoseDate"]) >= FDY_SWITCH_MIN_DAYS_SINCE_LAST_DOSE),
          then: { outcomeId: OUT_SWITCH_TOO_SOON },
        },
      ],
      default: Q_SWITCH_COUNSELLED,
    },

    {
      id: Q_SWITCH_COUNSELLED,
      type: "yes_no",
      text: "Has the patient been told that treatment restarts at 0.8 mg with the full titration, that titration takes at least 5 months, and that some weight regain during that period is likely? Document that this was explained.",
      hint: "Never start above 0.8 mg when switching. The dose then rises one step at a time, at least 30 days at each dose.",
      answerPolarity: "yes-positive",
      yes: downstream.entryNodeId,
      no: { outcomeId: FDY_OUT_CANNOT_SUPPLY },
      auditFlags: { yes: "fdy_switch_from_other_incretin" },
    },

    ...downstream.nodes,
  ],

  outcomes: {
    ...downstream.outcomes,
    [OUT_USE_TRANSFER]: foundayoRedirectOutcome(
      OUT_USE_TRANSFER,
      "Use the capsule transfer route — Foundayo®",
      "A patient already taking orforglipron capsules is changing formulation, not medicine. Do not start at 0.8 mg.",
      [
        "Start a Transfer consultation and supply the equivalent tablet strength.",
        "Capsules must never be supplied under this PGD: only film-coated tablets.",
      ],
    ),
    [OUT_SWITCH_TOO_SOON]: foundayoRedirectOutcome(
      OUT_SWITCH_TOO_SOON,
      "Cannot supply today — too soon after the last dose of the previous medicine",
      "The first 0.8 mg tablet must be taken no sooner than 7 days after the last dose of the previous incretin, and the previous medicine must not be taken alongside orforglipron.",
      [
        "Advise the patient not to take any more of the previous medicine.",
        "Arrange a return consultation no sooner than 7 days after the last dose.",
        "Document the date of the last dose and the advice given.",
      ],
    ),
  },
};
